import React from 'react';
import { ShieldX } from 'lucide-react';
import { Button, EmptyState } from '@/components/ds-dlp';
import { useAuth } from '../contexts/AuthContext';

interface RouteGuardProps {
  /**
   * Required permission code. Null = authenticated user, no specific
   * permission needed. Routes without auth at all bypass the guard
   * (e.g. /login, /setup) by simply not wrapping in RouteGuard.
   */
  perm: string | null;
  children: React.ReactNode;
}

/**
 * Permission-aware route wrapper. Three outcomes:
 *
 *   - Not authenticated → AppShell handles redirect to <Login>.
 *     RouteGuard only sees authenticated requests.
 *   - Authenticated, perm satisfied (or perm=null) → render children.
 *   - Authenticated, perm denied → render <Forbidden> with the required
 *     code, NOT a generic error. Helps users self-diagnose ("I need
 *     policy.write — let me ask my admin").
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({ perm, children }) => {
  const { hasPermission } = useAuth();
  if (perm && !hasPermission(perm)) {
    return <Forbidden requiredPerm={perm} />;
  }
  return <>{children}</>;
};

/** The 403, on ds EmptyState (cascade plan 3.5). */
const Forbidden: React.FC<{ requiredPerm: string }> = ({ requiredPerm }) => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <EmptyState
      icon={<ShieldX />}
      title="You don't have access to this page"
      description={
        <>
          This page requires the <code className="font-mono text-dense">{requiredPerm}</code> permission. Contact your
          tenant admin to request access.
        </>
      }
      action={
        <Button variant="secondary" onClick={() => window.history.back()}>
          Go back
        </Button>
      }
      headingLevel={2}
    />
  </div>
);
